import { Anchor, ActionIcon, Group, Stack, Text } from "@mantine/core";
import { Dropzone } from "@mantine/dropzone";
import { notifications } from "@mantine/notifications";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { IconX } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import type { Locale } from "@iso27001/shared";
import { attachmentsApi, type Attachment } from "../api/attachments.js";
import { useApiErrorMessage } from "../i18n/api-error.js";
import { formatDate } from "../i18n/format-date.js";

interface Props {
    entity: "ereignis" | "risiko" | "massnahme";
    entityId: number | null;
    canEdit: boolean;
}

function formatBytes(b: number): string {
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
    return `${(b / (1024 * 1024)).toFixed(1)} MB`;
}

export function AttachmentsSection({ entity, entityId, canEdit }: Props) {
    const qc = useQueryClient();
    const { t, i18n } = useTranslation();
    const locale = i18n.language as Locale;
    const apiError = useApiErrorMessage();

    const { data } = useQuery({
        queryKey: ["attachments", entity, entityId],
        queryFn: () => attachmentsApi.list(entity, entityId!),
        enabled: entityId !== null,
    });

    const attachments: Attachment[] = data?.items ?? [];

    async function handleDrop(files: File[]) {
        for (const file of files) {
            try {
                await attachmentsApi.upload(entity, entityId!, file);
                notifications.show({ message: t("attachments.notify.uploaded", { name: file.name }), color: "teal" });
            } catch (e) {
                notifications.show({
                    message: `"${file.name}": ${apiError(e, { fallback: "attachments.error.uploadFailed" })}`,
                    color: "red",
                });
            }
        }
        qc.invalidateQueries({ queryKey: ["attachments", entity, entityId] });
    }

    async function handleDelete(id: number, filename: string) {
        try {
            await attachmentsApi.remove(id);
            qc.invalidateQueries({ queryKey: ["attachments", entity, entityId] });
            notifications.show({ message: t("attachments.notify.deleted", { name: filename }), color: "teal" });
        } catch (e) {
            notifications.show({ message: apiError(e, { fallback: "attachments.error.deleteFailed" }), color: "red" });
        }
    }

    return (
        <Stack gap="xs">
            {canEdit && (
                <Dropzone onDrop={handleDrop} multiple>
                    <Text ta="center" size="sm" c="dimmed" py="xs">
                        {t("attachments.dropzoneLabel")}
                    </Text>
                </Dropzone>
            )}

            {attachments.length === 0 ? (
                <Text ta="center" c="dimmed" size="sm">
                    {t("attachments.empty")}
                </Text>
            ) : (
                <Stack gap={4}>
                    {attachments.map((a) => (
                        <Group key={a.id} justify="space-between" wrap="nowrap">
                            <Anchor
                                href={attachmentsApi.downloadUrl(a.id)}
                                download={a.filename}
                                target="_blank"
                                size="sm"
                                style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                            >
                                {a.filename}
                            </Anchor>
                            <Group gap="xs" wrap="nowrap" style={{ flexShrink: 0 }}>
                                <Text size="xs" c="dimmed">{formatBytes(a.sizeBytes)}</Text>
                                <Text size="xs" c="dimmed">{formatDate(a.uploadedAt, locale)}</Text>
                                {canEdit && (
                                    <ActionIcon
                                        size="xs"
                                        variant="subtle"
                                        color="red"
                                        onClick={() => handleDelete(a.id, a.filename)}
                                        aria-label={t("attachments.deleteAriaLabel")}
                                    >
                                        <IconX size={12} />
                                    </ActionIcon>
                                )}
                            </Group>
                        </Group>
                    ))}
                </Stack>
            )}
        </Stack>
    );
}
